// TIP-611 — `Your reference at this company`, the optional panel nested inside
// section 2. Not a numbered section: it sits inside the subject block.
//
// Three behaviours, in this order:
//   1. the email is entered first, on its own — nothing below it exists until
//      the value looks like an email address;
//   2. a match fills the fields it has details for and locks them, and draws
//      the green panel to say who was matched. There is no `Use these details`
//      button any more: the details are already in;
//   3. no match draws the coral panel and guesses the name from the local part.
//
// TIP-611 — the match is a real lookup now, not the DIRECTORY fixture:
// GET /api/briefs/requester-lookup/. It is debounced and only fires on a
// syntactically valid address, so the panel has a fourth, momentary state
// between 1 and 2/3 while the answer is out.
import React from "react";
import "./counterpartyBriefs.css";
import CbDialPhone from "./CbDialPhone";
import { CbTextField } from "./CbField";
import { ORDER_COPY } from "./orderFormFixtures";

const ReferenceContactPanel = ({ form, dialOptions }) => {
  const { values, onInput, onRefEmail, onRefName, set } = form;
  const copy = ORDER_COPY.reference;

  return (
    <div className="cb-ref-panel">
      <div className="cb-ref-head">
        <div>
          <h4 className="cb-ref-title">{copy.heading}</h4>
          <p className="cb-ref-sub">{copy.sub}</p>
        </div>
        <span className="cb-ref-optional">{copy.optional}</span>
      </div>

      <div className="cb-ref-body">
        <CbTextField
          id="cb-ref-email" error={form.errorFor("refEmail")}
          label={copy.email}
          inputMode="email"
          value={values.refEmail}
          placeholder={copy.emailPlaceholder}
          onChange={onRefEmail}
        />

        {/* In flight. Drawn instead of the no-match note, which would
            otherwise flash for the moment before the answer arrives and tell
            the customer we do not know an address we are still asking about. */}
        {form.lookupPending && (
          <p className="cb-ref-looking" data-testid="cb-ref-looking" role="status">
            {copy.looking}
          </p>
        )}

        {form.directoryHit && (
          <div className="cb-ref-match" data-testid="cb-ref-match">
            <span className="cb-ref-match-mark" aria-hidden="true">
              &#10003;
            </span>
            <div className="cb-ref-match-text">
              <p className="cb-ref-match-title">{copy.matchHeading}</p>
              <p className="cb-ref-match-line">{copy.matchLine(form.directoryHit)}</p>
              <p className="cb-ref-match-note">{copy.matchFilled}</p>
            </div>
          </div>
        )}

        {form.showGuessNote && (
          <div className="cb-ref-guess" data-testid="cb-ref-guess">
            <span className="cb-ref-guess-mark" aria-hidden="true">
              !
            </span>
            <p className="cb-ref-guess-text">{copy.noMatch}</p>
          </div>
        )}

        {/* The three fields the email unlocks. Absent from the DOM, not hidden:
            a blank contact name in an unopened panel would submit as one. */}
        {form.refEmailValid && (
          <div className="cb-grid cb-ref-fields" data-testid="cb-ref-fields">
            <CbTextField
              id="cb-ref-name" error={form.errorFor("refName")}
              label={copy.name}
              value={values.refName}
              placeholder={copy.namePlaceholder}
              onChange={onRefName}
              disabled={form.isLocked("refName")}
            />
            <CbTextField
              id="cb-ref-role" error={form.errorFor("refRole")}
              label={copy.role}
              value={values.refRole}
              placeholder={copy.rolePlaceholder}
              onChange={onInput("refRole")}
              disabled={form.isLocked("refRole")}
            />
            <CbDialPhone
              id="cb-ref-phone" error={form.errorFor("refPhone")}
              testId="cb-ref-phone"
              label={copy.mobile}
              dial={values.refDial}
              phone={values.refPhone}
              options={dialOptions}
              onDialChange={set("refDial")}
              onPhoneChange={set("refPhone")}
              disabledDial={form.isLocked("refDial")}
              disabled={form.isLocked("refPhone")}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ReferenceContactPanel;
